import { getTenantSlug } from './tenant.js';

/**
 * Notes de mise à jour (fenêtre « Nouveautés », menu de l'avatar).
 *
 * À chaque mise à jour qui change quelque chose de visible, ajouter une note
 * EN TÊTE de `RELEASE_NOTES`, dans le même changement que le code : elle part
 * avec le déploiement. Écrire pour les membres, pas pour l'équipe : ce qu'on
 * peut faire, où le trouver, au tutoiement, sans tiret cadratin ni jargon
 * technique. `admin: true` = visible des OWNER/ADMIN seulement (éditeur,
 * Admin, modération). Ne jamais changer l'`id` d'une note publiée : c'est lui
 * qui dit qui l'a déjà vue.
 */

export type ReleaseItemKind = 'new' | 'fix';

export interface ReleaseItem {
  kind: ReleaseItemKind;
  title: string;
  text: string;
  /** Réservé aux OWNER/ADMIN. */
  admin?: boolean;
}

export interface ReleaseNote {
  /** Stable, unique, croissant : date de la mise à jour (AAAA-MM-JJ), suffixée au besoin (« 2026-11-03-b »). */
  id: string;
  /** Jour affiché (AAAA-MM-JJ). */
  date: string;
  title: string;
  items: ReleaseItem[];
}

export const RELEASE_NOTES: ReleaseNote[] = [
  {
    id: '2026-11-03',
    date: '2026-11-03',
    title: 'Des réunions plus confortables',
    items: [
      {
        kind: 'new',
        title: 'Un vrai chat de réunion',
        text: 'Dans une salle de réunion, le bouton Chat (touche T) ouvre les messages sur le côté. Ils restent ouverts pendant que tu suis la réunion, et ceux qui arrivent en retard voient ce qui a déjà été écrit. Tout est effacé quand la salle se vide.',
      },
      {
        kind: 'new',
        title: 'Épingler un message, réserver le chat',
        text: 'Épingle un message en haut du chat de la réunion (le lien du jour, la consigne…), ou réserve l’écriture aux admins pendant une présentation.',
        admin: true,
      },
      {
        kind: 'new',
        title: 'Réactions sur les vidéos',
        text: 'En vue réunion, les réactions s’envolent depuis la vignette de la personne qui les envoie.',
      },
      {
        kind: 'new',
        title: 'Plein écran et zoom',
        text: 'Mets en plein écran le partage d’écran ou la caméra affichée en grand, et zoome dessus avec les boutons + et −, la molette ou le pincement.',
      },
      {
        kind: 'new',
        title: 'Tes emojis préférés à portée de main',
        text: 'Une seconde ligne avec les emojis que tu utilises le plus apparaît sous les réactions habituelles, sur le campus comme dans la messagerie.',
      },
      {
        kind: 'new',
        title: 'Le tableau des duos',
        text: 'À l’étage Duos de Copy Camp, approche-toi du tableau en liège et lis la liste des duos de la semaine.',
      },
      {
        kind: 'new',
        title: 'Remplir le tableau des duos',
        text: 'Après chaque tirage, ouvre le tableau puis Modifier : choisis les personnes de chaque duo (ou trio) et ajoute un mot. Le tableau se pose depuis l’éditeur (objet « Tableau de liège mural », catégorie « Réunion & présentation »).',
        admin: true,
      },
      {
        kind: 'new',
        title: 'Les tableaux de ton bureau',
        text: 'Les tableaux blancs de ton bureau personnel sont maintenant dans Mes tableaux blancs (menu de ton avatar), même si tu les as retirés du bureau.',
      },
      {
        kind: 'fix',
        title: 'Plus rien sous le chat',
        text: 'Quand le chat ou la messagerie est ouvert, ton avatar et la salle restent bien au centre de la place libre au lieu de passer sous le panneau.',
      },
      {
        kind: 'fix',
        title: 'Caméras et micros allumés en cours de route',
        text: 'Une caméra ou un micro allumé après être entré dans une salle arrive maintenant chez les autres sans devoir sortir et revenir.',
      },
      {
        kind: 'fix',
        title: 'Import des accès plus sûr',
        text: 'Quand tu importes une liste, une valeur du fichier qui ne porte pas le nom d’un profil reste « À choisir » : l’import attend ton choix au lieu de retirer leurs accès en plus aux comptes existants. L’aperçu dit aussi combien de comptes vont perdre un accès, et tes choix sont retenus pour le prochain import.',
        admin: true,
      },
    ],
  },
];

const isAdminRole = (role: string) => role === 'OWNER' || role === 'ADMIN';

/** Notes avec seulement ce que ce rôle doit voir (une note vide disparaît). */
export function notesFor(role: string): ReleaseNote[] {
  const admin = isAdminRole(role);
  return RELEASE_NOTES
    .map((n) => ({ ...n, items: n.items.filter((i) => admin || !i.admin) }))
    .filter((n) => n.items.length > 0);
}

const key = (userId: string) => `placio:release-notes:${getTenantSlug()}:${userId}`;

function lastSeen(userId: string): string | null {
  try { return localStorage.getItem(key(userId)); } catch { return null; }
}

/** La note la plus récente pour ce rôle n'a pas encore été vue sur ce navigateur. */
export function hasUnseenNotes(userId: string, role: string): boolean {
  const latest = notesFor(role)[0];
  if (!latest || !userId) return false;
  const seen = lastSeen(userId);
  return !seen || seen < latest.id;
}

/** Notes plus récentes que la dernière vue (toutes si rien n'a été vu). */
export function isNoteNew(userId: string, note: ReleaseNote): boolean {
  const seen = lastSeen(userId);
  return !seen || seen < note.id;
}

export function markNotesSeen(userId: string): void {
  const latest = RELEASE_NOTES[0];
  if (!latest || !userId) return;
  try { localStorage.setItem(key(userId), latest.id); } catch { /* navigation privée */ }
}

export function formatNoteDate(date: string): string {
  return new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${date}T12:00:00Z`));
}
